<template>
  <div class="app-main-inner dg-scheme-detail">
    <div class="app-main-inner-row">
      <div class="filters" />
      <div class="triggers">
        <el-button no="Add" :disabled="locked" type="primary" @click="toAdd">{{ $t('CargoMsg.Cargo_add') }}</el-button>
        <el-button no="Delete" :disabled="locked" type="danger" @click="toDelete">{{ $t('CargoMsg.Cargo_Delete') }}</el-button>
      </div>
    </div>
    <div class="app-main-inner-row row-grid" style="min-height: 400px; padding: 8px 0 0">
      <cargo-ag-grid
        config-module="100702"
        :grid-params.sync="gridParams"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :row-data="rowData"
      />
    </div>
  </div>
</template>

<script>
import { TYPE_NAMES } from '@/utils/class/AdvanceSelect'

import cargoAdvanceSelect from '@/components/Cargo/Common/cargoAdvanceSelect'
import lodash from 'lodash'
import $store from '@/store'
import ColumnUtils from '@/utils/ColumnUtils'
import {
  CHARGE_SCHEME_PARTNER_TYPES,
  ACCOUNT_CUSTOMER,
  getCommonState,
  CHARGE_SCHEME_PARTNER_TYPES_SE
} from '@/class/cargo/model/parameters/ChargeConstants'
import { formatNumberToThousands } from '@/utils'
import CargoAgGridCommonInputEditor from '@/components/Cargo/Common/CargoAgGridCommonInputEditor/index'
import CargoAgGridSelectEditor from '@/components/Cargo/Common/CargoAgGridAdvanceSelectEditor'
import CargoAgGridAdvanceSelectEditor from '@/components/Cargo/Common/CargoAgGridAdvanceSelectEditor'
import { exportExcel } from '@/utils/tableDataExportUtils'
import { cache, cacheName } from '@/utils/cacheManager'
import { getLocationList } from '@/api/location'
import { getObjectByCache } from '@/utils/basicDataCache'
import { ChargeUtils } from '@/class/cargo/model/utils/ChargeUtils'
import { chargeItemCompatibleLanguage } from '@/utils/commonUtil'

export default {
  name: 'DgSchemeDetail',
  components: { cargoAdvanceSelect },
  props: {
    status: { type: String, required: true },
    dgSchemeDetailValue: { type: Array, default: null, required: false },
    cityListOld: { type: Array, default: null },
    locked: { type: Boolean, default: false },
    lob: { type: String, default: '' },
    saleRoleHide: { type: Boolean, default: false } // 子应用销售角色
  },
  data() {
    return {
      gridParams: {},
      gridOptions: {
        rowSelection: 'single',
        suppressCellSelection: true,
        defaultColDef: {
          resizable: true,
          filter: true
        },
        pagination: false,
        // 单击显示编辑
        singleClickEdit: true,
        // 添加编辑组件
        frameworkComponents: {
          CargoAgGridCommonInputEditor,
          CargoAgGridSelectEditor,
          CargoAgGridAdvanceSelectEditor
        },
        onRowDataChanged: event => {
          // 清除单选行
          event.api.clearFocusedCell()
        },
        onCellFocused: event => {
          const nodes = event.api.rowModel.rowsToDisplay
          this.selectedNode = nodes[event.rowIndex] || null
        },
        onSortChanged: event => {
          // 重置单选行
          if (this.selectedNode) {
            event.api.setFocusedCell(this.selectedNode.rowIndex, event.columnApi.getAllDisplayedColumns()[0])
          }
        }
      },
      rowData: [],
      selectedNode: null,
      originRowData: [],
      schemeMaintainForm: {
        level: 0,
        receivable: false,
        chargeItemId: '',
        charge: '',
        partnerType: '',
        accountId: '',
        currency: '',
        measure: '',
        eqSize: '',
        eqType: '',
        quantity: 0.000,
        unitStep: 1,
        unitRate: 0.000,
        minimum: 0.00,
        remark: '',
        status: '',
        waitDealStatus: ''
      },
      inputIncludingTaxUnitPrice: 0, // 单价是否含税
      cityList: [], // 所在城市
      canEditEqSizeAndEqType: true,
      chargeItemSet: new Set() // 费用项目集合
    }
  },
  computed: {
    isZh() {
      return this.$store.getters.language === 'zh'
    },
    editSizeAndType() {
      return this.canEditEqSizeAndEqType
    },
    columnDefs() {
      const self = this
      let columnDefs = [
        {
          headerName: this.$t('CargoMsg.Cargo_SequenceNumber'),
          field: 'level',
          initialWidth: 130,
          // 是否开启编辑
          editable: !this.locked,
          // 单元格绑定编辑组件
          cellEditor: 'CargoAgGridCommonInputEditor',
          // 编辑组件入参
          cellEditorParams: {
            isNumber: true,
            inputProps: {
              precision: 0,
              max: 999999998,
              min: 0,
              controlsPosition: 'right',
              style: 'width: 100%'
            }
          },
          // 值变化事件
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          },
          // 显示数据格式化
          valueFormatter: params => {
            return params.value ? formatNumberToThousands(ColumnUtils.numberColumnLabelFunc(params.value, 0)) : 0
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_PayReceive'),
          field: 'receivable',
          initialWidth: 130,
          editable: !this.locked,
          cellEditor: 'CargoAgGridSelectEditor',
          cellEditorParams: {
            selectProps: {
              staticOptions: [{ name: this.$t('Message.Receivable'), code: 'true' }, { name: this.$t('Message.Payable'), code: 'false' }],
              tagName: 'name',
              tagValue: 'code',
              filterable: false
            }
          },
          // 值变化事件
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          },
          // 显示格式化
          valueFormatter: params => {
            return params.value ? this.$t('Message.Receivable') : this.$t('Message.Payable')
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_ChargeName'),
          field: 'chargeItemId',
          initialWidth: 180,
          editable: !this.locked,
          cellEditor: 'CargoAgGridSelectEditor',
          cellEditorParams: {
            selectProps: {
              staticOptions: [...this.chargeItemSet],
              callbackParams: { key: 'chargeItemId' },
              callbackMethodType: 'value',
              tagValue: 'itemId',
              tagName: this.$store.getters.language === 'zh' ? 'zhName' : 'enName',
              filterable: true,
              filterTagNames: ['chargecode', 'enName', 'zhName', 'cnShortName', 'scaleCode', 'languageName'],
              filterType: 'global',
              methodParams: { active: true }
            }
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          },
          valueFormatter: (params) => {
            return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_ChargeItem, 'itemId', params.value, 'zhName')
          }
        }, {
          headerName: this.$t('Payment.controlledOrNot'),
          colId: 'controlled',
          field: 'controlled',
          initialWidth: 80,
          cellClass: 'ag-right-aligned-cell',
          valueFormatter: (params) => {
            return params.value ? this.$t('SeaExport.Yes') : this.$t('SeaExport.No')
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_SettlementCompany') + this.$t('CargoMsg.Cargo_Type'),
          field: 'partnerType',
          initialWidth: 180,
          editable: !this.locked,
          cellEditor: 'CargoAgGridSelectEditor',
          cellEditorParams: {
            selectProps: {
              callbackParams: { key: 'partnerType' },
              callbackMethodType: 'value',
              tagName: this.isZh ? 'chineseName' : 'englishName',
              tagValue: 'code',
              staticOptions: this.returnListByLob()
            }
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          },
          valueFormatter: (params) => {
            let name = ''
            if (params.value) {
              const common = getCommonState(params.value, this.returnListByLob())
              if (common) {
                name = this.isZh ? common.chineseName : common.englishName
              }
            }
            return name
          }
        }, {
          headerName: this.$t('SeaExport.Account'),
          field: 'accountId',
          colId: 'accountId',
          width: 140,
          editable: !this.locked,
          cellEditor: 'CargoAgGridAdvanceSelectEditor',
          methodParams: { active: true },
          cellEditorParams: {
            selectProps: {
              selectType: TYPE_NAMES.basic_data_Company,
              tagValue: 'cmpIdx',
              tagName: 'chineseName',
              autoKey: 'auto_0',
              dataFilterFunction: (item, inputVal, data) => { // item为company，data为该ag-grid行的数据
                if (item.active) {
                  if (data.receivable) { // 应收
                    if (item.ifCustomer) { // 判断是 客户
                      return this.filterAgent(item, data)
                    }
                  } else { // 应付
                    if (item.ifSupplier) {
                      return this.filterAgent(item, data)
                    }
                  }
                }
                return false
              },
              filterable: true
            }
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          },
          valueFormatter: (params) => {
            return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_Company, 'cmpIdx', params.value, 'chineseName')
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_Currency'),
          field: 'currency',
          minWidth: 50,
          width: 100,
          editable: !this.locked,
          cellEditor: 'CargoAgGridSelectEditor',
          cellEditorParams: {
            selectProps: {
              selectType: TYPE_NAMES.basic_data_Currency,
              callbackParams: { key: 'currency' },
              callbackMethodType: 'value',
              filterable: true
            }
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          }
        },
        { headerName: this.$t('Pricing.BelongingCity'), field: 'cityCode', colId: 'cityCode', width: 180,
          editable: !this.locked,
          cellEditor: 'CargoAgGridAdvanceSelectEditor',
          cellEditorParams: {
            selectProps: {
              tagValue: 'code',
              tagName: this.$store.getters.language === 'zh' ? 'chineseName' : 'englishName',
              staticOptions: this.cityList,
              filterable: true,
              filterTagNames: ['code', 'chineseName', 'englishName']
            }
          },
          onCellValueChanged: params => this.onCellChange(params),
          valueFormatter: (params) => {
            if (!params.value) return ''
            const city = this.cityList.find(p => p.code === params.value)
            if (!city) return ''
            return this.$store.getters.language === 'zh' ? city.chineseName : city.englishName
          }
        }, {
          headerName: this.$t('Pricing.FeeUnit'),
          field: 'measure',
          initialWidth: 180,
          editable: !this.locked,
          cellEditor: 'CargoAgGridSelectEditor',
          cellEditorParams: {
            selectProps: {
              selectType: TYPE_NAMES.basic_data_ChargeUnit,
              tagValue: 'code',
              tagName: $store.getters.language === 'zh' ? 'chineseName' : 'englishName',
              methodParams: { unit: true, seaFreight: true },
              filterable: true
            }
          },
          onCellValueChanged: (params) => {
            // 变更时 如果不是指定箱则清空箱型
            if (params.data.measure !== 'BYCNTR') {
              params.data.eqSize = ''
              params.data.eqType = ''
              self.canEditEqSizeAndEqType = false
            } else {
              self.canEditEqSizeAndEqType = true
            }
            if (params.data.measure !== 'WGT' && params.data.measure !== 'WGTX' && params.data.measure !== 'VOL') {
              params.data.rangeGe = 0.0
              params.data.rangeLt = 0.0
            }
            this.onCellChange(params)
          },
          valueFormatter: (params) => {
            return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_ChargeUnit, 'code', params.value, 'chineseName')
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_RangeAndGE'),
          field: 'rangeGe',
          width: 100,
          editable: params => {
            return params.data.measure === 'WGT' || params.data.measure === 'WGTX' || params.data.measure === 'VOL' || params.data.measure === 'MDS'
          },
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: {
            isNumber: true,
            inputProps: {
              precision: 3,
              min: 0.000,
              controlsPosition: 'right',
              style: 'width: 100%'
            }
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_RangeAndLT'),
          field: 'rangeLt',
          width: 100,
          editable: params => {
            return params.data.measure === 'WGT' || params.data.measure === 'WGTX' || params.data.measure === 'VOL' || params.data.measure === 'MDS'
          },
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: {
            isNumber: true,
            inputProps: {
              precision: 3,
              min: 0.000,
              controlsPosition: 'right',
              style: 'width: 100%'
            }
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_ContainerSize'),
          field: 'eqSize',
          width: 100,
          editable: params => {
            return params.data.measure === 'BYCNTR'
          },
          cellEditor: 'CargoAgGridSelectEditor',
          cellEditorParams: {
            selectProps: {
              fields: ['code'],
              callbackParams: { key: 'eqSize' },
              callbackMethodType: 'value',
              tagName: 'code',
              tagValue: 'code',
              filterTagNames: ['code'],
              staticOptions: [
                { code: '20' },
                { code: '40' },
                { code: '45' },
                { code: '10' },
                { code: '53' },
                { code: '30' },
                { code: '48' }
              ]
            }
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_ContainerType'),
          field: 'eqType',
          width: 100,
          editable: params => {
            return params.data.measure === 'BYCNTR'
          },
          cellEditor: 'CargoAgGridSelectEditor',
          cellEditorParams: {
            selectProps: {
              selectType: TYPE_NAMES.basic_data_EQType,
              tagName: 'code',
              tagValue: 'code',
              callbackParams: { key: 'eqType' },
              callbackMethodType: 'value'
            }
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_AmountNumber'),
          field: 'quantity',
          width: 100,
          editable: !this.locked,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: {
            isNumber: true,
            inputProps: {
              precision: 3,
              min: 0.000,
              controlsPosition: 'right',
              style: 'width: 100%'
            }
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          },
          valueFormatter: params => {
            return ColumnUtils.number3ColumnLabelFunc(params.value)
          }
        }, {
          headerName: this.$t('SeaExport.MeasureUnit'),
          field: 'unitStep',
          width: 120,
          editable: !this.locked,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: {
            isNumber: true,
            inputProps: {
              precision: 0,
              min: 0,
              controlsPosition: 'right',
              style: 'width: 100%'
            }
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          },
          valueFormatter: params => {
            return params.value || '0'
          }
        }, {
          headerName: this.$t('CargoMsg.Cargo_UnitPrice'),
          field: 'unitRate',
          width: 100,
          editable: !this.locked,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: {
            isNumber: true,
            inputProps: {
              precision: 3,
              min: 0,
              controlsPosition: 'right',
              style: 'width: 100%'
            }
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          },
          valueFormatter: params => {
            return ColumnUtils.number3ColumnLabelFunc(params.value)
          }
        }, {
          headerName: this.$t('SeaExport.MiniMum'),
          field: 'minimum',
          width: 120,
          editable: !this.locked,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: {
            isNumber: true,
            inputProps: {
              precision: 2,
              min: 0,
              controlsPosition: 'right',
              style: 'width: 100%'
            }
          },
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          },
          valueFormatter: params => {
            return ColumnUtils.number2ColumnLabelFunc(params.value)
          }
        },
        {
          headerName: this.$t('FinanceManage.charge_unitPriceIncludeTax'),
          colId: 'unitPriceIncludeTax',
          field: 'unitPriceIncludeTax',
          initialWidth: 120,
          cellClass: 'ag-right-aligned-cell',
          valueFormatter: (params) => {
            return params.value ? this.$t('SeaExport.Yes') : this.$t('SeaExport.No')
          },
          editable: !this.locked && !!this.inputIncludingTaxUnitPrice,
          cellEditor: 'CargoAgGridAdvanceSelectEditor',
          cellEditorParams: {
            selectProps: {
              tagValue: 'code',
              tagName: 'value',
              staticOptions: [{ code: false, value: this.$t('SeaExport.No') }, { code: true, value: this.$t('SeaExport.Yes') }]
            }
          },
          onCellValueChanged: p => this.onCellChange(p),
          initialHide: !this.inputIncludingTaxUnitPrice,
          suppressColumnsToolPanel: !this.inputIncludingTaxUnitPrice
        },
        {
          headerName: this.$t('FinanceManage.charge_taxRate'), colId: 'taxRate', field: 'taxRate',
          valueFormatter: (params) => {
            return this.$numeral(params.value).format('0.00')
          },
          width: 120,
          editable: !this.locked,
          cellEditor: 'CargoAgGridCommonInputEditor',
          cellEditorParams: {
            isNumber: true,
            inputProps: {
              controls: false,
              precision: 2
            }
          },
          onCellValueChanged: p => this.onCellChange(p)
        }, {
          headerName: this.$t('CargoMsg.Cargo_Remarks'),
          field: 'remark',
          width: 120,
          editable: !this.locked,
          cellEditor: 'CargoAgGridCommonInputEditor',
          onCellValueChanged: (params) => {
            this.onCellChange(params)
          }
        }, {
          headerName: this.$t('FinanceManage.charge_drawABillOrNot'),
          field: 'needInvoice',
          colId: 'needInvoice',
          width: 120,
          cellClass: 'ag-right-aligned-cell',
          valueFormatter: params => {
            return params.value ? this.$t('SeaExport.Yes') : this.$t('SeaExport.No')
          },
          editable: !this.locked,
          cellEditor: 'CargoAgGridAdvanceSelectEditor',
          cellEditorParams: {
            selectProps: {
              tagValue: 'value',
              tagName: 'label',
              staticOptions: [{ value: 'true', label: this.$t('SeaExport.Yes') }, { value: 'false', label: this.$t('SeaExport.No') }]
            }
          },
          onCellValueChanged: p => this.onCellChange(p)
        }
      ]
      // 隐藏的设置列：固定结算单位
      if (this.saleRoleHide) {
        columnDefs = columnDefs.filter(item => !['accountId'].includes(item.field))
      }
      return columnDefs
    }
  },
  watch: {
    async status() {
      this.refresh()
    },
    lob(val) {
      this.rowData.forEach(row => {
        if (val !== 'SE' && row?.partnerType && (row.partnerType === 'nominatedAgent' || row.partnerType === 'exchangeAgent')) {
          row.partnerType = ''
        }
      })
      this.gridOptions.api.refreshCells({ force: true })
    }
  },
  async created() {
    this.cityList = this.cityListOld
    if (this.cityList || this.cityList.length <= 0) {
      this.cityList = await getLocationList({}).then((res) => {
        return res.data.list || []
      })
    }
    await cache.load(cacheName.CONFIG)
    this.inputIncludingTaxUnitPrice = parseInt(cache.config.get('InputIncludingTaxUnitPrice'))
    this.chargeItemSet = new Set(chargeItemCompatibleLanguage())
  },
  mounted() {
    this.refresh()
  },
  methods: {
    refresh() {
      if (['edit', 'copy'].includes(this.status)) {
        this.rowData = this.dgSchemeDetailValue
      }
      this.gridOptions.api.refreshCells({ force: true })
      this.originRowData = this.status === 'copy' ? [] : lodash.cloneDeep(this.rowData)
    },
    // 检查费用明细表单
    toCheckDgSchemeDetailForm(opt) { // opt=> add,edit
      const { chargeItemId, eqSize, eqType, currency, measure } = opt
      let errorMsg = ''
      if (!chargeItemId) {
        errorMsg = this.$t('CargoMsg.Cargo_PleaseChooseFeeName')
      }
      if (measure === 'BYCNTR' && !eqSize && !eqType) {
        errorMsg = errorMsg ? errorMsg + ',' + this.$t('CargoMsg.Cargo_PleaseEnterBoxTypeOrBoxType') : this.$t('CargoMsg.Cargo_PleaseEnterBoxTypeOrBoxType')
      }
      if (!currency) {
        errorMsg = errorMsg ? errorMsg + ',' + this.$t('CargoMsg.Cargo_PlsInput') + this.$t('CargoMsg.Cargo_Currency') : this.$t('CargoMsg.Cargo_PlsInput') + this.$t('CargoMsg.Cargo_Currency')
      }
      if (!measure) {
        errorMsg = errorMsg ? errorMsg + ',' + this.$t('Pricing.FeeUnit') + this.$t('CargoMsg.Cargo_CanNotEmpty') : this.$t('Pricing.FeeUnit') + this.$t('CargoMsg.Cargo_CanNotEmpty')
      }
      console.log('errorMsg', errorMsg)
      if (!errorMsg) {
        return true
      }
      this.$message.error(errorMsg)
      return false
    },
    // 保存前校验新增行
    async checkAddRow() {
      // 校验新增项
      // const arr = this.rowData.filter(row => row.waitDealStatus === 'add')
      const arr = this.rowData
      for (const row of arr) {
        if (!this.toCheckDgSchemeDetailForm(row)) {
          return false
        }
      }
      await this.notifyParent()
      return true
    },
    getValueFromChild(obj) {
      this.schemeMaintainForm[obj.key] = obj.value
    },
    toDelete() {
      const self = this
      if (!this.selectedNode || !this.selectedNode.data) {
        self.$message.warning(this.$t('CargoMsg.Cargo_PleaseChooseData'))
      }
      let index = 0
      for (let i = 0; i < this.rowData.length; i++) {
        if (lodash.isEqual(this.rowData[i], this.selectedNode.data)) {
          index = i
          break
        }
      }
      this.rowData.splice(index, 1)
      this.gridOptions.api.refreshCells({ force: true })
      this.notifyParent()
    },
    // 筛选出新增&和删除项并返还给父组件
    async notifyParent() {
      let addRow = []
      if (this.rowData && this.rowData.length > 0) {
        if (this.originRowData && this.originRowData.length > 0) {
          addRow = this.rowData.filter(row => {
            const rows = this.originRowData.filter(originRow => {
              return JSON.stringify(originRow) === JSON.stringify(row)
            })
            return rows.length < 1
          })
        } else {
          addRow = [...this.rowData]
        }
      }
      let delRow = []
      if (this.originRowData && this.originRowData.length > 0) {
        if (this.rowData && this.rowData.length > 0) {
          delRow = this.originRowData.filter(originRow => {
            const rows = this.rowData.filter(row => {
              return originRow.uuid === row.uuid
            })
            return rows.length < 1
          })
        } else {
          delRow = this.originRowData
        }
      }
      const rows = []
      addRow.forEach(row => {
        rows.push({
          waitDealStatus: 'add',
          ...row
        })
      })
      delRow.forEach(row => {
        rows.push({
          waitDealStatus: 'delete',
          ...row
        })
      })
      this.$emit('func', rows)
    },
    // 导出excel操作处理器
    exportHandler() {
      const self = this
      if (this.rowData.length === 0) {
        self.$message.warning(this.$t('CargoMsg.Cargo_NoData'))
        return
      }
      const rows = lodash.cloneDeep(this.rowData)
      const timestamp = new Date().getTime()
      console.log('====开始时间======', timestamp)
      // 获取显示列
      const allDisplayedColumns = this.gridOptions.columnApi.getAllDisplayedColumns()
      // 获取选中行
      const fileName = this.$t('Information.FeeDetailsXls')
      exportExcel(fileName, rows, allDisplayedColumns, true)
    },
    initSchemeMaintainForm() {
      return {
        level: 0,
        receivable: false,
        chargeItemId: '',
        charge: '',
        partnerType: ACCOUNT_CUSTOMER.code,
        accountId: '',
        currency: '',
        // measure: 'BYCNTR',
        eqSize: '',
        eqType: '',
        quantity: 0.000,
        unitStep: 1,
        unitRate: 0.000,
        minimum: 0.00,
        rangeGe: 0.00,
        rangeLt: 0.00,
        remark: '',
        status: '',
        waitDealStatus: '',
        needInvoice: true,
        unitPriceIncludeTax: this.inputIncludingTaxUnitPrice === 2
      }
    },
    onCellChange(params) {
      const { node } = params
      const cost = params.data
      switch (params.colDef.field) {
        case 'chargeItemId': {
          const chargeItem = getObjectByCache(TYPE_NAMES.basic_data_ChargeItem, 'itemId', cost.chargeItemId)
          cost.taxRate = chargeItem ? (chargeItem.taxRate || 0) : 0
          // CARGO-31003 在新增费用时，直接对特定的费用进行受控
          cost.controlled = chargeItem?.controlled
          cost.needInvoice = chargeItem?.needInvoice
          break
        }
        case 'accountId': {
          ChargeUtils.changeChargeRateByAccountId(cost)
          break
        }
        case 'receivable':
          this.receivable = cost.receivable
          cost.partnerType = ''
          cost.accountId = 0
      }
      if (node.data.uuid) {
        node.data.waitDealStatus = 'edit'
      }
      if (this.gridOptions) this.gridOptions.api.refreshCells({ force: true }) // 刷新
    },
    toAdd() {
      const newRow = { ...this.initSchemeMaintainForm() }
      newRow.level = this.rowData.length ? this.rowData.length + 1 : 1
      newRow.waitDealStatus = 'add'
      this.rowData.push(newRow)
    },
    filterAgent(item, data) {
      if ((data.partnerType === 'nominatedAgent')) { // 结算单位类型勾选了指定货代理
        return !!item.designationAgent
      }
      if ((data.partnerType === 'exchangeAgent')) { // 结算单位类型勾选了换单代理
        return !!item.bookingAgent
      }
      return true
    },
    returnListByLob() {
      return this.lob === 'SE' ? Array.from(CHARGE_SCHEME_PARTNER_TYPES_SE) : Array.from(CHARGE_SCHEME_PARTNER_TYPES)
    }
  }
}
</script>

<style lang="scss">
.dg-scheme-detail{
  .el-scrollbar__wrap{
    overflow-x: hidden;
  }
}
</style>
